import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { Divider } from "@src/appV2/redesign/components/Divider";
import { Text } from "@src/appV2/redesign/components/Text";

import { formatMessageDate, isSameDay } from "../utils/formatMessageTime";

interface ChatDateSeparatorProps {
  /** Unix timestamp in milliseconds for the message that follows this separator. */
  timestamp: number;
  /**
   * IANA time zone of the workplace (e.g. "America/New_York"). When provided,
   * the date label and day-grouping comparisons are computed in this time zone
   * so chat messages are bucketed by workplace-local day. When omitted, falls
   * back to the viewer's device time zone.
   */
  timeZone?: string;
}

/**
 * Renders a centered date label flanked by horizontal lines.
 * Displays "Today", "Yesterday", "January 15", or "January 15, 2023".
 */
export function ChatDateSeparator(props: ChatDateSeparatorProps) {
  const { timestamp, timeZone } = props;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        paddingY: 3,
      }}
    >
      <Divider sx={{ flex: 1 }} />
      <Text
        semibold
        variant="caption"
        sx={(theme) => ({
          color: theme.palette.base?.inactive,
          flexShrink: 0,
          whiteSpace: "nowrap",
        })}
      >
        {formatMessageDate(timestamp, timeZone)}
      </Text>
      <Divider sx={{ flex: 1 }} />
    </Box>
  );
}

/**
 * Determines whether a date separator should be rendered before a message.
 * Returns true for the first message or when the day changes between consecutive messages.
 * When `timeZone` is provided, the day comparison is done in that IANA time zone.
 */
export function shouldShowDateSeparator(
  currentTimestamp: number,
  previousTimestamp: number | undefined,
  timeZone?: string
): boolean {
  return !isDefined(previousTimestamp) || !isSameDay(previousTimestamp, currentTimestamp, timeZone);
}
